/**
 * Ghost pages — a planned page is a page in the deck at low fidelity.
 *
 * The slide is the layout's blank page with the planned title set and `_plan` on it (description = the
 * writing brief, notes, live data, chart style). It sits in the filmstrip like any page, the ordinary
 * page tools work on it (move, delete, title, layout), and "Build" writes the real page in its place.
 * Ghost pages are never exported, presented or checked — the server filters `_plan` out.
 */
import type { PlanPage, GhostPlan } from '@/cowork/omniad/api/coworkApi';
import { LAYOUT_CATALOG } from './layouts';

type Slide = Record<string, any>;

export const isGhost = (s: Slide | undefined | null): s is Slide & { _plan: GhostPlan } => !!s && !!s._plan && typeof s._plan === 'object';

/** A planned page as a slide: the layout's blank page, its title, and the plan riding along. */
export function ghostSlide(p: PlanPage): Slide {
  const entry = LAYOUT_CATALOG.find((l) => l.id === p.layout) || LAYOUT_CATALOG.find((l) => l.id === 'blank')!;
  const base = entry.make();
  const plan: GhostPlan = { description: p.description, notes: p.notes, needsData: p.needsData, chartStyle: p.chartStyle, elements: p.elements, layout: entry.id };
  // The title lands where the layout keeps it (a big-stat page has none; a cover uses `title` too).
  return { ...base, title: p.title, _plan: plan };
}

/** Re-shape a ghost page onto another layout, keeping its title and plan. */
export function reshapeGhost(s: Slide, layoutId: string): Slide {
  const entry = LAYOUT_CATALOG.find((l) => l.id === layoutId); if (!entry || !isGhost(s)) return s;
  const base = entry.make(); const t = base.type;
  const prev = { text: true, image: false, chart: false, table: false, ...(s._plan.elements || {}) };
  const elements = { ...prev, chart: prev.chart || t === 'chart', image: prev.image || t === 'image', table: prev.table || t === 'table' };
  return { ...base, title: s.title, _plan: { ...s._plan, layout: entry.id, elements } };
}
